import React from 'react';
import PropTypes from 'prop-types';
import { EVTypography, EVBox } from '@eagleview/ev-comp-library';
import { withTranslation } from 'react-i18next';
import { compose } from 'redux';
import EagleViewSupportLink from 'commonComponents/EagleViewSupportLink/EagleViewSupportLink.component';
import useStyles from './DisplayError.style';

/**
 * DisplayError Component - renders a centered error message with optional support link
 * This component displays error messages in a consistent, centered layout
 */
const DisplayError = ({
  errorMsg,
  errorDesc,
  showSupportLink = true,
  contentWidth,
  t,
}) => {
  const classes = useStyles();

  // Defensive programming - ensure errorMsg is defined
  if (!errorMsg) {
    return null;
  }

  // Safely translate the error message
  const translatedErrorMsg = t ? t(errorMsg) : errorMsg;
  const translatedErrorDesc = errorDesc && t ? t(errorDesc) : errorDesc;

  return (
    showSupportLink ? (
      <EVBox id="noAppsMessage" className={contentWidth}>
        <EVTypography>
          {translatedErrorMsg}
        </EVTypography>
        {translatedErrorDesc && (
          <EVTypography>
            {translatedErrorDesc}
          </EVTypography>
        )}
        {showSupportLink && (
          <div className={classes.supportLinkContainer}>
            <EagleViewSupportLink />
          </div>
        )}
      </EVBox>
    ) : (
      <div id="noAppsMessage" className={contentWidth} dangerouslySetInnerHTML={{ __html: translatedErrorMsg }} />
    )
  );
};

DisplayError.propTypes = {
  /**
   * The error errorMsg to display (translation key)
   */
  errorMsg: PropTypes.string.isRequired,

  /**
   * The error errorDesc to display (translation key, optional)
   */
  errorDesc: PropTypes.string,

  /**
   * Whether to show the support link
   */
  showSupportLink: PropTypes.bool,

  /**
   * CSS class name or style object for content width styling
   */
  contentWidth: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.shape({
      maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      marginLeft: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      marginTop: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      fontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ]),

  /**
   * Translation function
   */
  t: PropTypes.func.isRequired,
};

DisplayError.defaultProps = {
  errorDesc: '',
  showSupportLink: true,
  contentWidth: {},
};

export default compose(withTranslation())(DisplayError);
